import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert, IconButton, Tooltip, TextField, Stack, Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Refresh as RefreshIcon, FilterAlt as FilterIcon } from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { BarChart } from '@mui/x-charts/BarChart';
import { apiService } from '../../services/api';

const money = (n: number | null | undefined): string =>
  (n === null || n === undefined) ? '—' : 'RD$' + Math.round(n).toLocaleString('es-DO');

// Primer y último día del mes actual (rango por defecto)
function monthRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const to = now.toISOString().slice(0, 10);
  return { from, to };
}

const FunnelPage: React.FC = () => {
  const def = monthRange();
  const [from, setFrom] = useState(def.from);
  const [to, setTo] = useState(def.to);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFunnel = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getSocialFunnel({ from, to });
      setData(res?.data || null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Error al cargar el embudo');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchFunnel(); }, [fetchFunnel]);

  const rows = useMemo(() => (data?.rows || []).map((r: any, i: number) => ({
    id: r.adId || `row-${i}`,
    nombre: r.nombre || r.adId || '(desconocido)',
    gasto: r.gasto, leads: r.leads, cierres: r.cierres,
    costoPorCierre: r.costoPorCierre, neto: r.neto,
    alto: r.porInteres?.alto || 0, medio: r.porInteres?.medio || 0, bajo: r.porInteres?.bajo || 0,
  })), [data]);

  const adsOff = data && data.adSpendAvailable === false;

  const columns: GridColDef[] = [
    { field: 'nombre', headerName: 'Anuncio', flex: 1.4, minWidth: 180 },
    { field: 'gasto', headerName: 'Gasto', width: 110, type: 'number', valueFormatter: (v: any) => money(v) },
    { field: 'leads', headerName: 'Leads', width: 80, type: 'number' },
    {
      field: 'cierres', headerName: 'Cierres', width: 85, type: 'number',
      // El cierre se cuenta el día en que el cliente se dio de alta, no el día en que entró el
      // lead: un anuncio puede enseñar cierres con 0 leads en el rango (el lead era de antes).
      description: 'Clientes dados de alta en el rango que venían de este anuncio. Se cuentan el día del alta, aunque el lead sea de semanas antes.',
    },
    { field: 'costoPorCierre', headerName: 'Costo/cierre', width: 120, type: 'number', valueFormatter: (v: any) => money(v) },
    { field: 'neto', headerName: 'Neto', width: 120, type: 'number', valueFormatter: (v: any) => money(v) },
    { field: 'alto', headerName: 'Int. alto', width: 85, type: 'number' },
    { field: 'medio', headerName: 'Int. medio', width: 90, type: 'number' },
    { field: 'bajo', headerName: 'Int. bajo', width: 85, type: 'number' },
  ];

  // Chart 1: neto por anuncio (con signo → verde ≥0, rojo <0)
  const netoRows = rows.filter((r: any) => r.neto !== null && r.neto !== undefined).slice(0, 10);
  // Chart 2: leads por nivel de interés (suma de todos los anuncios)
  const interes = rows.reduce((acc: any, r: any) => {
    acc.alto += r.alto; acc.medio += r.medio; acc.bajo += r.bajo; return acc;
  }, { alto: 0, medio: 0, bajo: 0 });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, flexGrow: 1 }}>Embudo por anuncio</Typography>
        <Tooltip title="Refrescar"><IconButton onClick={fetchFunnel}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      {/* Filtros de fecha en una fila */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
          <TextField label="Desde" type="date" size="small" value={from}
            onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Hasta" type="date" size="small" value={to}
            onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <IconButton color="primary" onClick={fetchFunnel}><FilterIcon /></IconButton>
          {data && (
            <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
              <Chip size="small" label={`Cobertura atribución: ${data.attributionCoverage ?? '—'}%`} />
              {data.sinAdId ? <Chip size="small" color="warning" label={`${data.sinAdId} sin ad_id`} /> : null}
            </Stack>
          )}
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {adsOff && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Gasto/ROI en "—" hasta conectar la cuenta de anuncios al usuario del sistema. Leads, cierres y categorización sí están.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Neto por anuncio (RD$)</Typography>
                {netoRows.length ? (
                  <BarChart
                    height={300}
                    xAxis={[{ scaleType: 'band', data: netoRows.map((r: any) => r.nombre.slice(0, 16)) }]}
                    yAxis={[{ colorMap: { type: 'piecewise', thresholds: [0], colors: ['#c62828', '#2e7d32'] }, valueFormatter: (v: number) => money(v) }]}
                    series={[{ data: netoRows.map((r: any) => Math.round(r.neto)), label: 'Neto', valueFormatter: (v: any) => money(v) }]}
                    margin={{ left: 70 }}
                  />
                ) : <Typography variant="body2" color="text.secondary">Sin datos de neto (conecta ads).</Typography>}
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Leads por nivel de interés</Typography>
                <BarChart
                  height={300}
                  xAxis={[{ scaleType: 'band', data: ['Alto', 'Medio', 'Bajo'] }]}
                  series={[{ data: [interes.alto, interes.medio, interes.bajo], label: 'Leads' }]}
                  colors={['#2e7d32', '#ef6c00', '#c62828']}
                />
              </Paper>
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 1 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              autoHeight
              density="compact"
              initialState={{ sorting: { sortModel: [{ field: 'neto', sort: 'desc' }] } }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
            />
          </Paper>
        </>
      )}
    </Box>
  );
};

export default FunnelPage;
